import React, { useEffect, useState, useCallback } from 'react';
import { Box, useToast, Spinner, VStack } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import { useApp } from '../context/AppContext';
import { finalizarAtendimento as finalizarAtendimentoApi } from '../api/finalizarAtendimento';

// Componentes
import ServiceSelection from '../components/checkout/ServiceSelection';
import ProductCart from '../components/checkout/ProductCart';
import CheckoutHeader from '../components/checkout/CheckoutHeader';
import CheckoutButton from '../components/checkout/CheckoutButton';
import TotalGeral from '../components/checkout/TotalGeral';
import AdditionalServices from '../components/checkout/AdditionalServices';


const CheckoutPage = () => {
  const [servicos, setServicos] = useState([]);
  const [servicosPacote, setServicosPacote] = useState([]);
  const [pacoteAtivo, setPacoteAtivo] = useState(null);
  const [selected, setSelected] = useState([]);
  const [additionalSelected, setAdditionalSelected] = useState([]); // Novo estado para serviços adicionais
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const { atendimentoId, setAtendimentoId, logoutUser, cartProducts, removeProductFromCart, addProductToCart, decreaseProductQuantity, clearCartProducts, currentUser, isMensalista } = useApp();

  // NOVO: Controle de uso dos serviços do pacote
  const [usosServicos, setUsosServicos] = useState({});

  // Função utilitária para obter o mês atual no formato YYYY-MM
  function getMesReferenciaAtual() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }

  useEffect(() => {
    async function fetchServicos() {
      setLoading(true);
      const { data: servicosData, error: errorServicos } = await supabase.from('servicos').select('*').order('nome');
      if (!errorServicos) setServicos(servicosData);
      if (isMensalista && currentUser) {
        let mensalistaData = null;
        if (currentUser.id) {
          const result = await supabase
            .from('mensalistas')
            .select('id, pacote_id, pacotes_mensalistas(id, nome, descricao, pacote_servicos(servico_id, quantidade_inclusa))')
            .eq('id', currentUser.id)
            .single();
          mensalistaData = result.data;
        } else if (currentUser.name) {
          const nomeBusca = `%${currentUser.name}%`;
          const result = await supabase
            .from('mensalistas')
            .select('id, pacote_id, pacotes_mensalistas(id, nome, descricao, pacote_servicos(servico_id, quantidade_inclusa))')
            .ilike('nome', nomeBusca)
            .single();
          mensalistaData = result.data;
        }
        if (mensalistaData && mensalistaData.pacote_id && mensalistaData.pacotes_mensalistas) {
          setPacoteAtivo(mensalistaData.pacotes_mensalistas);
          setServicosPacote(mensalistaData.pacotes_mensalistas.pacote_servicos || []);

          // NOVO: Buscar período vigente do mensalista
          const { data: periodoMensalista, error: errorPeriodo } = await supabase
            .from('mensalistas')
            .select('data_inicio, data_fim')
            .eq('id', mensalistaData.id)
            .single();
          if (errorPeriodo || !periodoMensalista) {
            console.error('Erro ao buscar período do mensalista:', errorPeriodo);
            toast({ title: 'Erro ao buscar período do mensalista', description: errorPeriodo?.message, status: 'error' });
            setUsosServicos({});
          } else {
            const { data_inicio, data_fim } = periodoMensalista;
            // Buscar usos do pacote dentro do período de vigência
            const { data: usos, error: errorUsos } = await supabase
              .from('mensalista_pacote_servico_uso')
              .select('servico_id, quantidade_utilizada, created_at')
              .eq('mensalista_id', mensalistaData.id)
              .gte('created_at', data_inicio)
              .lte('created_at', data_fim)
              .order('created_at', { ascending: false });
            if (errorUsos) {
              console.error('Erro ao buscar usos:', errorUsos);
              toast({ title: 'Erro ao buscar usos do pacote', description: errorUsos.message, status: 'error' });
            }
            // Montar objeto: { servico_id: total_utilizado }
            const usosMap = {};
            if (usos && Array.isArray(usos)) {
              usos.forEach(u => {
                if (u.servico_id && u.quantidade_utilizada) {
                  usosMap[u.servico_id] = (usosMap[u.servico_id] || 0) + u.quantidade_utilizada;
                }
              });
            }
            setUsosServicos(usosMap);
          }
        } else {
          setPacoteAtivo(null);
          setServicosPacote([]);
          setUsosServicos({});
        }
      } else {
        setPacoteAtivo(null);
        setServicosPacote([]);
        setUsosServicos({});
      }
      setLoading(false);
    }
    fetchServicos();
  }, [isMensalista, currentUser]);

  const handleCheckout = useCallback(async () => {
    if (!atendimentoId) {
      toast({ title: 'Nenhum atendimento ativo.', status: 'warning' });
      return;
    }
    if (selected.length === 0 && additionalSelected.length === 0 && cartProducts.length === 0) {
      toast({ title: 'Selecione pelo menos um serviço, serviço adicional ou produto.', status: 'warning' });
      return;
    }
    setLoading(true);
    try {
      // Salva os serviços realizados no atendimento (incluindo os adicionais para mensalistas)
      const allServices = [...selected, ...additionalSelected];
      for (const servicoId of allServices) {
        await supabase.from('atendimentos_servicos').insert({ atendimento_id: atendimentoId, servico_id: servicoId });
      }
      // Salva os produtos comprados no atendimento
      for (const prod of cartProducts) {
        await supabase.from('atendimentos_produtos').insert({ atendimento_id: atendimentoId, produto_id: prod.id, quantidade: prod.quantity || 1 });
      }
      // --- REGISTRO DE USO DO PACOTE DO MENSALISTA ---
      if (isMensalista && currentUser && selected.length > 0) {
        let mensalistaId = currentUser.id;
        let podeRegistrarUsoPacote = true;
        
        // Se não houver id, buscar pelo nome
        if (!mensalistaId && currentUser.name) {
          const { data: mensalistaData, error } = await supabase
            .from('mensalistas')
            .select('id')
            .ilike('nome', currentUser.name)
            .single();
          if (mensalistaData && mensalistaData.id) {
            mensalistaId = mensalistaData.id;
          }
          if (error) {
            console.error('Erro ao buscar mensalista pelo nome:', error);
            toast({ title: 'Erro ao buscar mensalista pelo nome', description: error.message, status: 'error' });
            podeRegistrarUsoPacote = false;
          }
        }
        
        if (mensalistaId && podeRegistrarUsoPacote) {
          // Buscar o pacote_id do mensalista
          const { data: mensalistaData, error: mensalistaError } = await supabase
            .from('mensalistas')
            .select('pacote_id')
            .eq('id', mensalistaId)
            .single();
          
          if (mensalistaError) {
            console.error('Erro ao buscar pacote_id do mensalista:', mensalistaError);
            toast({ title: 'Erro ao buscar pacote do mensalista', status: 'error' });
            podeRegistrarUsoPacote = false;
          } else {
            const pacoteId = mensalistaData?.pacote_id;
            if (!pacoteId) {
              console.error('Mensalista não possui pacote_id');
              toast({ title: 'Mensalista não possui pacote associado', status: 'error' });
              podeRegistrarUsoPacote = false;
            } else {
              // Só registra uso se tudo estiver ok
              for (const servicoId of selected) {
                const mesReferencia = getMesReferenciaAtual();
                // Verificar se já existe um registro para este serviço neste mês
                const { data: existingUso } = await supabase
                  .from('mensalista_pacote_servico_uso')
                  .select('id, quantidade_utilizada')
                  .eq('mensalista_id', mensalistaId)
                  .eq('pacote_id', pacoteId)
                  .eq('servico_id', servicoId)
                  .eq('mes_referencia', mesReferencia)
                  .maybeSingle();

                let usoError;
                if (existingUso) {
                  // Se já existe, atualiza a quantidade
                  const { error } = await supabase
                    .from('mensalista_pacote_servico_uso')
                    .update({ quantidade_utilizada: existingUso.quantidade_utilizada + 1 })
                    .eq('id', existingUso.id);
                  usoError = error;
                } else {
                  // Se não existe, insere novo registro
                  const insertData = {
                    mensalista_id: mensalistaId,
                    pacote_id: pacoteId,
                    servico_id: servicoId,
                    quantidade_utilizada: 1,
                    mes_referencia: mesReferencia,
                  };
                  console.log('Tentando inserir em mensalista_pacote_servico_uso:', insertData);
                  const { error } = await supabase.from('mensalista_pacote_servico_uso').insert(insertData);
                  usoError = error;
                }
                if (usoError) {
                  console.error('Erro ao inserir uso do pacote:', JSON.stringify(usoError, null, 2));
                  console.error('Detalhes do erro - code:', usoError.code);
                  console.error('Detalhes do erro - message:', usoError.message);
                  console.error('Detalhes do erro - details:', usoError.details);
                  console.error('Detalhes do erro - hint:', usoError.hint);
                  toast({ title: 'Erro ao registrar uso do pacote', description: `${usoError.message} (${usoError.code})`, status: 'error' });
                } else {
                  toast({ title: 'Uso do pacote registrado!', status: 'success' });
                }
              }
            }
          }
        } else if (isMensalista) {
          console.warn('mensalistaId não encontrado para usuário mensalista:', currentUser);
          toast({ title: 'mensalistaId não encontrado', description: 'Não foi possível identificar o mensalista.', status: 'error' });
        }
      }
      // --- FIM REGISTRO USO PACOTE ---
      await finalizarAtendimentoApi(atendimentoId);
      setAtendimentoId(null);
      clearCartProducts();
      toast({ title: 'Atendimento finalizado com sucesso!', status: 'success' });
      logoutUser();
      navigate('/');
    } catch (e) {
      toast({ title: 'Erro ao finalizar atendimento', description: e.message, status: 'error' });
    }
    setLoading(false);
  }, [atendimentoId, selected, additionalSelected, cartProducts, toast, navigate, logoutUser, clearCartProducts, setAtendimentoId, isMensalista, currentUser]);

  // Verificar se pode finalizar o atendimento
  const canCheckout = atendimentoId && (selected.length > 0 || additionalSelected.length > 0 || cartProducts.length > 0);

  return (
    <Box
      minH="100vh"
      width="100%"
      bg="black"
      overflowY="auto"
      overflowX="hidden"
      p={4}
      className="hide-scrollbar"
      sx={{
        // Garantir que o conteúdo não fique embaixo do botão fixo
        paddingBottom: '120px',
      }}
    >
      <VStack spacing={4} align="stretch" className="hide-scrollbar">
        <CheckoutHeader />

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minH="200px">
            <Spinner size="xl" color="yellow.400" thickness="4px" />
          </Box>
        ) : (
          <ServiceSelection
            isMensalista={isMensalista}
            pacoteAtivo={pacoteAtivo}
            servicosPacote={servicosPacote}
            servicos={servicos}
            usosServicos={usosServicos}
            selected={selected}
            setSelected={setSelected}
            cartProducts={cartProducts}
          />
        )}

        <ProductCart
          cartProducts={cartProducts}
          addProductToCart={addProductToCart}
          decreaseProductQuantity={decreaseProductQuantity}
          removeProductFromCart={removeProductFromCart}
          selected={selected}
        />
        
        <AdditionalServices
          servicos={servicos}
          additionalSelected={additionalSelected}
          setAdditionalSelected={setAdditionalSelected}
          isMensalista={isMensalista}
        />
        
        <TotalGeral
          servicos={servicos}
          selected={selected}
          additionalSelected={additionalSelected}
          cartProducts={cartProducts}
          isMensalista={isMensalista}
        />

        <CheckoutButton 
          handleCheckout={handleCheckout} 
          loading={loading} 
          isDisabled={!canCheckout}
        />
      </VStack>
    </Box>
  );
}

export default CheckoutPage;
